import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineFilter, HiOutlineX } from 'react-icons/hi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import CustomDropdownWithSearch from '../../components/CustomDropdownWithSearch/CustomDropdownWithSearch';
import MultiSelectDropdown from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import {
  fetchAllPenetrationData,
  AllPenetrationData,
  SiteMetrics,
  RetailerPenetrationRow,
  ChannelPenetrationRow,
  type SolutionRow,
  type GeoPenetrationRow,
  type BrandSummaryRow,
  filterPois,
  aggregateRetailers,
  aggregateChannels,
  aggregateSolutions,
  aggregateGeo,
  aggregateBrandSummary,
} from '../../services/penetrationReportService';
import Spinner from '../../components/Spinner/Spinner';
import './PenetrationReport.css';

// ── Metric Card ───────────────────────────────────────────────────
function MetricCard({ label, value, subLabel, subValue, valueColor }: {
  label: string; value: string | number;
  subLabel?: string; subValue?: string | number; valueColor?: string;
}) {
  return (
    <div className="pr-metric-card">
      <span className="pr-metric-label">{label}</span>
      <span className="pr-metric-value" style={valueColor ? { color: valueColor } : undefined}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
      {subLabel && subValue !== undefined && (
        <span className="pr-metric-sub">
          {subLabel}: <strong>{typeof subValue === 'number' ? subValue.toLocaleString() : subValue}</strong>
        </span>
      )}
    </div>
  );
}

// ── Overall Penetration Donut ─────────────────────────────────────
function OverallDonut({ acquired, total }: { acquired: number; total: number }) {
  const remaining = Math.max(total - acquired, 0);
  const pct = total > 0 ? (acquired / total) * 100 : 0;
  const data = [{ name: 'Acquired', value: acquired }, { name: 'Remaining', value: remaining }];
  return (
    <div className="pr-overall-card">
      <div className="pr-overall-title">Overall Penetration Rate</div>
      <div className="pr-overall-body">
        <div style={{ width: 180, height: 180, position: 'relative', flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" innerRadius={52} outerRadius={80}
                startAngle={90} endAngle={-270} dataKey="value" strokeWidth={0}>
                <Cell fill="#16a34a" /><Cell fill="#ea580c" />
              </Pie>
              <ReTooltip formatter={(v, n) => [(v as number).toLocaleString(), n as string]}
                contentStyle={{ fontSize: '0.75rem', borderRadius: 8, border: '1px solid #e5e7eb' }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ fontSize: '1.375rem', fontWeight: 700, color: '#111827', lineHeight: 1 }}>{pct.toFixed(1)}%</span>
            <span style={{ fontSize: '0.6875rem', color: '#6b7280', marginTop: 2 }}>Penetration</span>
          </div>
        </div>
        <div className="pr-overall-legend">
          <div className="pr-overall-legend-item">
            <span className="pr-overall-legend-count" style={{ color: '#16a34a' }}>{acquired.toLocaleString()}</span>
            <span className="pr-overall-legend-desc">acquired</span>
          </div>
          <div className="pr-overall-legend-item">
            <span className="pr-overall-legend-count" style={{ color: '#ea580c' }}>{remaining.toLocaleString()}</span>
            <span className="pr-overall-legend-desc">remaining</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Retailer Chart ────────────────────────────────────────────────
function RetailerChart({ rows, onRetailerClick }: {
  rows: RetailerPenetrationRow[]; onRetailerClick: (name: string) => void;
}) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const data = rows.map(r => ({ name: r.name, acquired: r.acquired, notAcquired: r.notAcquired }));
  const chartH = Math.max(data.length * 36 + 48, 180);
  return (
    <div className="pr-chart-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="pr-chart-title">Retailer-Wise Penetration</div>
        <div className="pr-filter-chips">
          <button className={`pr-filter-chip ${view === 'chart' ? 'active' : ''}`} onClick={() => setView('chart')}>Chart</button>
          <button className={`pr-filter-chip ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')}>Table</button>
        </div>
      </div>
      {view === 'chart' ? (
        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          <ResponsiveContainer width="100%" height={chartH}>
            <BarChart layout="vertical" data={data} margin={{ top: 4, right: 40, left: 4, bottom: 8 }}
              onClick={(e: any) => { if (e?.activeLabel) onRetailerClick(e.activeLabel); }}
              style={{ cursor: 'pointer' }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={130} tick={({ x, y, payload }: any) => (
                <text x={(x as number) - 6} y={y} dy={4} textAnchor="end" fontSize={12} fill="#003da6"
                  style={{ cursor: 'pointer' }} onClick={() => onRetailerClick(payload.value)}>
                  {payload.value.length > 18 ? payload.value.slice(0, 16) + '…' : payload.value}
                </text>
              )} />
              <ReTooltip cursor={{ fill: '#f3f4f6' }} content={({ active, payload, label }: any) => {
                if (!active || !payload?.length) return null;
                const acq = payload.find((p: any) => p.dataKey === 'acquired')?.value ?? 0;
                const na  = payload.find((p: any) => p.dataKey === 'notAcquired')?.value ?? 0;
                const t = acq + na;
                return (
                  <div className="pr-tooltip">
                    <div className="pr-tooltip-title">{label}</div>
                    <div className="pr-tooltip-row"><span style={{ color: '#003da6' }}>Acquired</span><strong>{acq.toLocaleString()}</strong></div>
                    <div className="pr-tooltip-row"><span style={{ color: '#ea580c' }}>Non-Acquired</span><strong>{na.toLocaleString()}</strong></div>
                    <div className="pr-tooltip-row"><span>Penetration</span><strong>{t > 0 ? ((acq / t) * 100).toFixed(1) : '0.0'}%</strong></div>
                  </div>
                );
              }} />
              <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: '0.75rem', paddingTop: 8 }} />
              <Bar dataKey="acquired" name="Acquired" stackId="a" fill="#003da6" />
              <Bar dataKey="notAcquired" name="Non-Acquired" stackId="a" fill="#ea580c" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="pr-table-wrap" style={{ maxHeight: 480, overflowY: 'auto' }}>
          <table className="pr-table">
            <thead><tr>
              <th>Retailer</th>
              <th className="pr-th-num">Total Sites</th>
              <th className="pr-th-num">Acquired</th>
              <th className="pr-th-num">Non-Acquired</th>
              <th className="pr-th-num">Penetration</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="pr-td-empty">No data</td></tr>
              ) : rows.map(r => (
                <tr key={r.name} style={{ cursor: 'pointer' }} onClick={() => onRetailerClick(r.name)}>
                  <td className="pr-td-name pr-drill-link">{r.name}</td>
                  <td className="pr-td-num">{r.totalSites.toLocaleString()}</td>
                  <td className="pr-td-num">{r.acquired.toLocaleString()}</td>
                  <td className="pr-td-num">{r.notAcquired.toLocaleString()}</td>
                  <td className="pr-td-num"><span className="pr-pct-badge">{r.pct.toFixed(1)}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Solution Chart ────────────────────────────────────────────────
function SolutionChart({ rows }: { rows: SolutionRow[] }) {
  return (
    <div className="pr-chart-card">
      <div className="pr-chart-title">Solution Penetration</div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} margin={{ top: 16, right: 24, left: 0, bottom: 4 }} barCategoryGap="40%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
          <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={52}
            domain={[0, (max: number) => Math.ceil(Math.max(max, 1) / 5) * 5]} tickFormatter={(v: number) => `${v}%`} />
          <ReTooltip cursor={{ fill: '#f3f4f6' }} content={({ active, payload, label }: any) => {
            if (!active || !payload?.length) return null;
            const row = rows.find(r => r.name === label);
            if (!row) return null;
            return (
              <div className="pr-tooltip">
                <div className="pr-tooltip-title">{label}</div>
                <div className="pr-tooltip-row"><span>Installed</span><strong>{row.installed.toLocaleString()}</strong></div>
                <div className="pr-tooltip-row"><span>Not Installed</span><strong>{row.notInstalled.toLocaleString()}</strong></div>
                <div className="pr-tooltip-row"><span>Penetration</span><strong>{row.pct.toFixed(1)}%</strong></div>
              </div>
            );
          }} />
          <Bar dataKey="pct" name="Penetration %" radius={[5, 5, 0, 0]} maxBarSize={64}
            label={({ x, y, width, index }: any) => (
              <text x={(x as number) + (width as number) / 2} y={(y as number) - 6}
                textAnchor="middle" fontSize={12} fill="#374151">
                {rows[index]?.pct.toFixed(1)}%
              </text>
            )}>
            {rows.map((r, i) => <Cell key={i} fill={r.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Channel Chart ─────────────────────────────────────────────────
function ChannelChart({ rows }: { rows: ChannelPenetrationRow[] }) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const data = rows.map(r => ({ name: r.channel, Customer: r.customer, 'Non-Customer': r.nonCustomer }));
  return (
    <div className="pr-chart-card pr-channel-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="pr-chart-title">Channel-Wise Penetration</div>
        <div className="pr-filter-chips">
          <button className={`pr-filter-chip ${view === 'chart' ? 'active' : ''}`} onClick={() => setView('chart')}>Chart</button>
          <button className={`pr-filter-chip ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')}>Table</button>
        </div>
      </div>
      {view === 'chart' ? (
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false}
              angle={-45} textAnchor="end" interval={0} height={80} />
            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={52} />
            <ReTooltip formatter={(v, n) => [(v as number).toLocaleString(), n as string]}
              contentStyle={{ fontSize: '0.75rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
              cursor={{ fill: '#f3f4f6' }} />
            <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: '0.75rem' }} />
            <Bar dataKey="Customer" fill="#003da6" radius={[3, 3, 0, 0]} maxBarSize={40} />
            <Bar dataKey="Non-Customer" fill="#ea580c" radius={[3, 3, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="pr-table-wrap">
          <table className="pr-table">
            <thead><tr>
              <th>Channel</th>
              <th className="pr-th-num">Customer</th>
              <th className="pr-th-num">Non-Customer</th>
              <th className="pr-th-num">Total</th>
              <th className="pr-th-num">Penetration</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="pr-td-empty">No data</td></tr>
              ) : rows.map(r => {
                const total = r.customer + r.nonCustomer;
                const pct = total > 0 ? (r.customer / total) * 100 : 0;
                return (
                  <tr key={r.channel}>
                    <td className="pr-td-name">{r.channel}</td>
                    <td className="pr-td-num">{r.customer.toLocaleString()}</td>
                    <td className="pr-td-num">{r.nonCustomer.toLocaleString()}</td>
                    <td className="pr-td-num">{total.toLocaleString()}</td>
                    <td className="pr-td-num"><span className="pr-pct-badge">{pct.toFixed(1)}%</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Geo Chart ─────────────────────────────────────────────────────
function GeoChart({ rows }: { rows: GeoPenetrationRow[] }) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const data = rows.map(r => ({ name: r.state, acquired: r.acquired, notAcquired: r.notAcquired }));
  const chartH = Math.max(data.length * 28 + 48, 200);
  return (
    <div className="pr-chart-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="pr-chart-title">Geographic Penetration (by State)</div>
        <div className="pr-filter-chips">
          <button className={`pr-filter-chip ${view === 'chart' ? 'active' : ''}`} onClick={() => setView('chart')}>Chart</button>
          <button className={`pr-filter-chip ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')}>Table</button>
        </div>
      </div>
      {view === 'chart' ? (
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          <ResponsiveContainer width="100%" height={chartH}>
            <BarChart layout="vertical" data={data} margin={{ top: 4, right: 72, left: 4, bottom: 4 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={120}
                tick={{ fontSize: 11, fill: '#374151', fontWeight: 500 }} axisLine={false} tickLine={false} />
              <ReTooltip cursor={{ fill: '#f3f4f6' }} content={({ active, payload, label }: any) => {
                if (!active || !payload?.length) return null;
                const acq = (payload.find((p: any) => p.dataKey === 'acquired')?.value as number) ?? 0;
                const na  = (payload.find((p: any) => p.dataKey === 'notAcquired')?.value as number) ?? 0;
                const t = acq + na;
                return (
                  <div className="pr-tooltip">
                    <div className="pr-tooltip-title">{label}</div>
                    <div className="pr-tooltip-row"><span style={{ color: '#003da6' }}>Acquired</span><strong>{acq.toLocaleString()}</strong></div>
                    <div className="pr-tooltip-row"><span style={{ color: '#ea580c' }}>Non-Acquired</span><strong>{na.toLocaleString()}</strong></div>
                    <div className="pr-tooltip-row"><span>Penetration</span><strong>{t > 0 ? ((acq / t) * 100).toFixed(1) : '0.0'}%</strong></div>
                  </div>
                );
              }} />
              <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: '0.75rem', paddingTop: 8 }} />
              <Bar dataKey="acquired" name="Acquired" stackId="a" fill="#003da6" />
              <Bar dataKey="notAcquired" name="Non-Acquired" stackId="a" fill="#ea580c" radius={[0, 3, 3, 0]}
                label={({ x, y, width, height, index }: any) => {
                  const row = data[index];
                  if (!row) return null;
                  const t = row.acquired + row.notAcquired;
                  if (t === 0) return null;
                  return (
                    <text x={(x as number) + (width as number) + 6} y={(y as number) + (height as number) / 2}
                      dy={4} fontSize={10} fill="#6b7280" textAnchor="start">
                      {((row.acquired / t) * 100).toFixed(0)}%
                    </text>
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="pr-table-wrap" style={{ maxHeight: 500, overflowY: 'auto' }}>
          <table className="pr-table">
            <thead><tr>
              <th>State</th>
              <th className="pr-th-num">Total Sites</th>
              <th className="pr-th-num">Acquired</th>
              <th className="pr-th-num">Non-Acquired</th>
              <th className="pr-th-num">Penetration</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="pr-td-empty">No data</td></tr>
              ) : rows.map(r => (
                <tr key={r.state}>
                  <td className="pr-td-name">{r.state}</td>
                  <td className="pr-td-num">{r.totalSites.toLocaleString()}</td>
                  <td className="pr-td-num">{r.acquired.toLocaleString()}</td>
                  <td className="pr-td-num">{r.notAcquired.toLocaleString()}</td>
                  <td className="pr-td-num"><span className="pr-pct-badge">{r.pct.toFixed(1)}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Brand Summary Table ──────────────────────────────────────────
function BrandSummaryTable({ rows, onBrandClick }: { rows: BrandSummaryRow[]; onBrandClick: (name: string) => void }) {
  const [search, setSearch] = useState('');
  const filtered = search
    ? rows.filter(r => r.brandName.toLowerCase().includes(search.toLowerCase()))
    : rows;

  return (
    <div className="pr-chart-card">
      <div className="pr-table-header">
        <div className="pr-chart-title">Brand Penetration Details</div>
        <input
          className="pr-table-search"
          placeholder="Search brand..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div className="pr-table-wrap" style={{ maxHeight: 520, overflowY: 'auto' }}>
        <table className="pr-table">
          <thead>
            <tr>
              <th>Brand Name</th>
              <th className="pr-th-num">Total Stores</th>
              <th className="pr-th-num">Acquired Stores</th>
              <th className="pr-th-num">Penetration Rate</th>
              <th className="pr-th-num">Total Install Count</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="pr-td-empty">No data</td></tr>
            ) : filtered.map(r => (
              <tr key={r.brandName} style={{ cursor: 'pointer' }} onClick={() => onBrandClick(r.brandName)}>
                <td className="pr-td-name pr-drill-link">{r.brandName}</td>
                <td className="pr-td-num">{r.totalStores.toLocaleString()}</td>
                <td className="pr-td-num">{r.acquiredStores.toLocaleString()}</td>
                <td className="pr-td-num"><span className="pr-pct-badge">{r.penetrationRate.toFixed(1)}%</span></td>
                <td className="pr-td-num">{r.totalInstallCount.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Filter Panel ──────────────────────────────────────────────────
function PrFilterPanel({
  isOpen, onClose,
  retailers, setRetailers,
  channels, setChannels,
  states, setStates,
  allRetailers, allChannels, allStates,
  activeCount,
}: {
  isOpen: boolean; onClose: () => void;
  retailers: string[]; setRetailers: (v: string[]) => void;
  channels: string[];  setChannels:  (v: string[]) => void;
  states: string[];    setStates:    (v: string[]) => void;
  allRetailers: string[]; allChannels: string[]; allStates: string[];
  activeCount: number;
}) {
  const handleClear = () => { setRetailers([]); setChannels([]); setStates([]); };

  return (
    <>
      {isOpen && <div className="filter-config-overlay" onClick={onClose} />}
      <div className={`filter-config-panel ${isOpen ? 'open' : ''}`}>

        <div className="filter-config-header">
          <div className="header-left-group">
            <div className="header-icon"><HiOutlineFilter /></div>
            <div className="header-left-f">
              <h2>Filters</h2>
              <p className="header-subtitle">Refine all charts</p>
            </div>
            {activeCount > 0 && <span className="active-filters-badge">{activeCount}</span>}
          </div>
          <button className="close-btn" onClick={onClose}><HiOutlineX /></button>
        </div>

        {activeCount > 0 && (
          <div className="clear-filters-section">
            <button className="clear-all-filters-btn" onClick={handleClear}>
              <HiOutlineX /> Clear All Filters
            </button>
          </div>
        )}

        <div className="filter-config-content">
          <div className="filters-tab">

            {activeCount > 0 && (
              <div className="active-filters-display">
                <div className="active-filters-title">Active Filters</div>
                <div className="active-filters-list">
                  {retailers.length > 0 && (
                    <span className="active-filter-chip">
                      Retailers: {retailers.length}
                      <button className="filter-chip-remove" onClick={() => setRetailers([])}><HiOutlineX /></button>
                    </span>
                  )}
                  {channels.length > 0 && (
                    <span className="active-filter-chip">
                      Channels: {channels.length}
                      <button className="filter-chip-remove" onClick={() => setChannels([])}><HiOutlineX /></button>
                    </span>
                  )}
                  {states.length > 0 && (
                    <span className="active-filter-chip">
                      States: {states.length}
                      <button className="filter-chip-remove" onClick={() => setStates([])}><HiOutlineX /></button>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Retailer */}
            <div className="filter-section">
              <div className="section-header">
                <h3>Ultimate Parent / Retailer</h3>
                {retailers.length > 0 && <span className="active-filter-tag">{retailers.length}</span>}
              </div>
              <CustomDropdownWithSearch
                value={retailers} options={allRetailers} onChange={setRetailers}
                placeholder="All retailers" showTags={false}
              />
            </div>

            {/* Channel */}
            <div className="filter-section">
              <div className="section-header">
                <h3>Channel</h3>
                {channels.length > 0 && <span className="active-filter-tag">{channels.length}</span>}
              </div>
              <MultiSelectDropdown
                values={channels} options={allChannels} onChange={setChannels}
                placeholder="All channels" hasSelectAll
              />
            </div>

            {/* State */}
            <div className="filter-section">
              <div className="section-header">
                <h3>State</h3>
                {states.length > 0 && <span className="active-filter-tag">{states.length}</span>}
              </div>
              <CustomDropdownWithSearch
                value={states} options={allStates} onChange={setStates}
                placeholder="All states" showTags={false}
              />
            </div>

          </div>
        </div>

        <div className="filter-actions">
          <button className="close-panel-btn" onClick={onClose}>Close</button>
          <button className="apply-filters-btn" onClick={onClose}>Apply</button>
        </div>
      </div>
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────
export default function PenetrationReport() {
  const navigate = useNavigate();
  const [data, setData]       = useState<AllPenetrationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [retailers, setRetailers] = useState<string[]>([]);
  const [channels,  setChannels]  = useState<string[]>([]);
  const [states,    setStates]    = useState<string[]>([]);

  const activeFilterCount =
    (retailers.length > 0 ? 1 : 0) +
    (channels.length  > 0 ? 1 : 0) +
    (states.length    > 0 ? 1 : 0);

  useEffect(() => {
    fetchAllPenetrationData().then(setData).finally(() => setLoading(false));
  }, []);

  // Cross-filter: apply ALL filters to a single poi array, then re-aggregate
  const filteredPois = useMemo(() => {
    if (!data) return [];
    return filterPois(data.rawPois, data.brandMap, data.zipToState, retailers, channels, states);
  }, [data, retailers, channels, states]);

  const filteredRetailers = useMemo(() =>
    data ? aggregateRetailers(filteredPois, data.brandMap) : [],
    [filteredPois, data]
  );

  const filteredChannels = useMemo(() =>
    aggregateChannels(filteredPois),
    [filteredPois]
  );

  const filteredSolutions = useMemo(() =>
    aggregateSolutions(filteredPois),
    [filteredPois]
  );

  const filteredGeo = useMemo(() =>
    data ? aggregateGeo(filteredPois, data.zipToState) : [],
    [filteredPois, data]
  );

  const filteredBrandSummary = useMemo(() =>
    aggregateBrandSummary(filteredPois),
    [filteredPois]
  );

  // Recalculate KPI metrics from filtered pois
  const filteredMetrics = useMemo(() => {
    if (!data) return null;
    const hasFilters = retailers.length > 0 || channels.length > 0 || states.length > 0;
    if (!hasFilters) return data.metrics; // no filters → use server aggregates (includes period data)

    const pois = filteredPois;
    const total = pois.length;
    const acquired = pois.filter(p => !!p.customer_id).length;
    const purchekInst = pois.filter(p => !!p.purchek_install_date).length;
    const containmentInst = pois.filter(p => !!p.containment_install_date).length;
    const cartMgrInst = pois.filter(p => !!p.cart_manager_install_date).length;
    const subInst = pois.filter(p => !!p.subscription_start_date).length;

    return {
      serviceableSites: total,
      acquiredCustomerSites: acquired,
      acquiredInPeriod: data.metrics.acquiredInPeriod,
      acquiredInPrevPeriod: data.metrics.acquiredInPrevPeriod,
      newSiteOpeningsInPeriod: data.metrics.newSiteOpeningsInPeriod,
      newSiteOpeningsInPrevPeriod: data.metrics.newSiteOpeningsInPrevPeriod,
      purchek: { installed: purchekInst, notInstalled: total - purchekInst },
      containment: { installed: containmentInst, notInstalled: total - containmentInst },
      cartManager: { installed: cartMgrInst, notInstalled: total - cartMgrInst },
      subscription: { installed: subInst, notInstalled: total - subInst },
    } as SiteMetrics;
  }, [data, filteredPois, retailers, channels, states]);

  const handleRetailerClick = (name: string) =>
    navigate(`/dashboard/opportunitydetails/${encodeURIComponent(name)}`);

  const metrics = filteredMetrics;

  return (
    <div className="pr-page">
      <div className="pr-header">
        <div>
          <h1 className="pr-title">Penetration Report</h1>
          <p className="pr-subtitle">Site acquisition and solution penetration by retailer and channel</p>
        </div>
        <button className="pr-filter-btn" onClick={() => setFilterPanelOpen(true)}>
          <HiOutlineFilter />
          Filters
          {activeFilterCount > 0 && <span className="pr-filter-btn-badge">{activeFilterCount}</span>}
        </button>
      </div>

      <PrFilterPanel
        isOpen={filterPanelOpen} onClose={() => setFilterPanelOpen(false)}
        retailers={retailers} setRetailers={setRetailers}
        channels={channels}   setChannels={setChannels}
        states={states}       setStates={setStates}
        allRetailers={data?.allRetailers ?? []}
        allChannels={data?.allChannels ?? []}
        allStates={data?.allStates ?? []}
        activeCount={activeFilterCount}
      />

      {loading ? (
        <div className="pr-loading"><Spinner /></div>
      ) : metrics && data && (
        <>
          {/* KPI + donut */}
          <div className="pr-top-row">
            <div className="pr-metrics-grid">
              <MetricCard label="# Serviceable Sites" value={metrics.serviceableSites} valueColor="var(--gk-blue)" />
              <MetricCard label="# Acquired Customer Sites" value={metrics.acquiredCustomerSites} valueColor="#16a34a" />
              <MetricCard label="# Acquired Sites in Period" value={metrics.acquiredInPeriod} subLabel="Prev Period" subValue={metrics.acquiredInPrevPeriod} />
              <MetricCard label="New Site Openings in Period" value={metrics.newSiteOpeningsInPeriod} subLabel="Prev Period" subValue={metrics.newSiteOpeningsInPrevPeriod} />
            </div>
            <OverallDonut acquired={metrics.acquiredCustomerSites} total={metrics.serviceableSites} />
          </div>

          {/* Solution chart */}
          <SolutionChart rows={filteredSolutions} />

          {/* Retailer chart */}
          <RetailerChart rows={filteredRetailers} onRetailerClick={handleRetailerClick} />

          {/* Channel chart */}
          <ChannelChart rows={filteredChannels} />

          {/* Geo chart */}
          <GeoChart rows={filteredGeo} />

          {/* Brand detail table */}
          <BrandSummaryTable rows={filteredBrandSummary} onBrandClick={handleRetailerClick} />
        </>
      )}
    </div>
  );
}
